import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { Button } from '@fluentui/react-northstar';
import cn from 'classnames';
import { semantic } from '../../tokens';
import { resolveIcon } from '../TFIcon/resolveIcon';
import './TFButton.less';
/** Vendor mapping — replaced wholesale at design system migration (`adapter.v9.tsx` / `adapter.shadcn.tsx`). `TFButtonProps` in, vendor JSX out; the public API never leaks here. */ var SIZE_MAP = {
    sm: 'small',
    md: 'medium',
    lg: 'medium' // Northstar has no 'large' size
};
/** One entry per `TFButtonVariant` (TS enforces exhaustiveness — a new variant added to `types.ts` without a row here is a compile error, not a silently unstyled button). Colors are static semantic tokens, so this is data, not logic. */ var VARIANT_STYLES = {
    primary: {
        enabled: {
            backgroundColor: semantic.colorBrandBackground
        },
        disabled: {
            backgroundColor: semantic.colorNeutralBackground1Disabled,
            color: semantic.colorNeutralForeground1Disabled
        }
    },
    secondary: {
        enabled: {
            backgroundColor: semantic.colorNeutralBackground1,
            color: semantic.colorNeutralForeground1,
            borderColor: semantic.colorNeutralStroke1
        },
        disabled: {
            backgroundColor: semantic.colorNeutralBackground1Disabled,
            color: semantic.colorNeutralForeground1Disabled,
            borderColor: semantic.colorNeutralStroke1Disabled
        }
    },
    outline: {
        enabled: {
            color: semantic.colorBrandStroke1,
            borderColor: semantic.colorBrandStroke1,
            backgroundColor: 'transparent'
        },
        disabled: {
            color: semantic.colorNeutralForegroundDisabled,
            borderColor: semantic.colorNeutralForegroundDisabled,
            backgroundColor: 'transparent'
        }
    },
    subtle: {
        enabled: {
            backgroundColor: semantic.colorNeutralBackground1,
            color: semantic.colorNeutralForeground2,
            borderColor: 'transparent'
        },
        disabled: {
            backgroundColor: semantic.colorNeutralBackground1Disabled,
            color: semantic.colorNeutralForegroundDisabled,
            borderColor: 'transparent'
        }
    },
    // `transparent` and `link` share the same colors; only their box shape (TFButton.less) differs.
    transparent: {
        enabled: {
            color: semantic.colorBrandForeground1,
            backgroundColor: 'transparent'
        },
        disabled: {
            color: semantic.colorNeutralForegroundDisabled,
            backgroundColor: 'transparent'
        }
    },
    link: {
        enabled: {
            color: semantic.colorBrandForeground1,
            backgroundColor: 'transparent'
        },
        disabled: {
            color: semantic.colorNeutralForegroundDisabled,
            backgroundColor: 'transparent'
        }
    },
    danger: {
        enabled: {
            backgroundColor: semantic.colorNeutralBackground1,
            color: semantic.colorPaletteRedForeground1,
            borderColor: semantic.colorPaletteRedForeground1
        },
        disabled: {
            backgroundColor: semantic.colorNeutralBackground1Disabled,
            color: semantic.colorNeutralForegroundDisabled,
            borderColor: semantic.colorNeutralForegroundDisabled
        }
    },
    // Gradient tokens are `background-image` values, so these two use the `background` shorthand, not `backgroundColor`.
    // Disabled drops the gradient entirely and mirrors `primary` / `outline` respectively.
    ai: {
        enabled: {
            background: semantic.colorAiBackground,
            color: semantic.colorNeutralForegroundOnBrand,
            border: 'none',
            borderColor: 'transparent'
        },
        disabled: {
            backgroundColor: semantic.colorNeutralBackground1Disabled,
            color: semantic.colorNeutralForeground1Disabled,
            borderColor: 'transparent'
        }
    },
    // Border: the gradient sits on the border-box layer under an opaque `colorNeutralBackground1` padding-box fill (the
    // `MeetingIntelligenceBox.less` technique — not a `::before` mask, which would clobber Northstar's own `:before`/`:after` focus ring).
    // Text/icon: `TFButton.less` clips `colorAiForeground` to the glyphs; `color` here is the solid fallback an SVG icon (not a font glyph) inherits.
    'ai-outline': {
        enabled: {
            background: "linear-gradient(".concat(semantic.colorNeutralBackground1, ", ").concat(semantic.colorNeutralBackground1, ") padding-box, ").concat(semantic.colorAiStroke, " border-box"),
            color: semantic.colorBrandForeground1,
            // The stroke IS the border-box layer, so the border must keep its 1px width — `border: 'none'` collapses it to 0 and the gradient vanishes.
            border: '1px solid transparent'
        },
        disabled: {
            color: semantic.colorNeutralForegroundDisabled,
            borderColor: semantic.colorNeutralForegroundDisabled,
            backgroundColor: 'transparent'
        }
    },
    // Pastel gradient fill with brand text; hover lives in TFButton.less.
    'ai-subtle': {
        enabled: {
            background: semantic.colorAiSubtleBackground,
            color: semantic.colorBrandForeground1,
            border: 'none',
            borderColor: 'transparent'
        },
        disabled: {
            backgroundColor: semantic.colorNeutralBackground1Disabled,
            color: semantic.colorNeutralForeground1Disabled,
            borderColor: 'transparent'
        }
    }
};
/** Variant colors are set inline (not a LESS class) since inline style always wins over Northstar's runtime-generated classes. */ var variantStyle = function variantStyle(variant, disabled) {
    return VARIANT_STYLES[variant][disabled ? 'disabled' : 'enabled'];
};
/** Zeroes Northstar's own icon-slot margin so `.tf-button`'s `gap: 8px` (TFButton.less) is the only icon-label spacing, and flex-centers each slot independently (avoids a `lineHeight` fix, which drifts per icon type — SVG vs font glyph). */ var SLOT_CENTERED = {
    margin: 0,
    display: 'inline-flex',
    alignItems: 'center'
};
/** Dims a disabled icon via opacity — icons are opaque SVG/font glyphs, so the disabled text `color` swap never reaches them. */ var ICON_DISABLED_STYLE = {
    opacity: semantic.disabledOpacity
};
export var renderTFButton = function renderTFButton(_0) {
    var _0_variant = _0.variant, variant = _0_variant === void 0 ? 'secondary' : _0_variant, _0_size = _0.size, size = _0_size === void 0 ? 'md' : _0_size, children = _0.children, iconBefore = _0.iconBefore, iconAfter = _0.iconAfter, loading = _0.loading, disabled = _0.disabled, fullWidth = _0.fullWidth, onClick = _0.onClick, _0_type = _0.type, type = _0_type === void 0 ? 'button' : _0_type, id = _0.id, className = _0.className, title = _0.title, style = _0.style, rest = _object_without_properties(_0, [
        "variant",
        "size",
        "children",
        "iconBefore",
        "iconAfter",
        "loading",
        "disabled",
        "fullWidth",
        "onClick",
        "type",
        "id",
        "className",
        "title",
        "style"
    ]);
    var _rest_arialabel;
    var iconSlot = iconBefore !== null && iconBefore !== void 0 ? iconBefore : iconAfter;
    var resolvedIcon = resolveIcon(iconSlot);
    var iconAfterOnly = !!(iconAfter && !iconBefore);
    // Neither Fluent v9 nor shadcn has a separate icon-button component — passing an icon with no
    // label is what makes a button icon-only, so it's derived here rather than a caller-set flag.
    var iconOnly = !children && !!resolvedIcon;
    if (process.env.NODE_ENV !== 'production' && iconOnly && !rest['aria-label'] && !title) {
        console.warn('TFButton: icon-only button has no accessible name — pass `title` or `aria-label`.');
    }
    var hasIconAndLabel = !!resolvedIcon && !!children;
    // Raw falsy `children` (not a `{children:false}` shorthand) skips Northstar's content slot entirely — a shorthand object always renders a phantom span that still claims a `gap: 8px` next to the icon.
    // Same phantom-slot issue for the icon during `loading`: dropped entirely rather than styled to zero-width, since Northstar's own hide-while-loading style still reserves its margin/gap.
    // The wrapped icon shorthand is only used when OUR styles need to reach that slot (has a label to center against, or disabled) — gated on `!!resolvedIcon` so a button with no icon never gets an empty phantom slot either.
    var iconStyleOverrides = _object_spread({}, hasIconAndLabel && SLOT_CENTERED, disabled && ICON_DISABLED_STYLE);
    var iconShorthand = resolvedIcon && (hasIconAndLabel || disabled) ? {
        children: resolvedIcon,
        styles: iconStyleOverrides
    } : resolvedIcon;
    var iconProp = loading ? undefined : iconShorthand;
    var contentProp = hasIconAndLabel ? {
        children: children,
        styles: SLOT_CENTERED
    } : children;
    return /*#__PURE__*/ _jsx(Button, _object_spread_props(_object_spread({}, rest), {
        id: id,
        type: type,
        title: title,
        // 'outline'/'danger'/'subtle'/'ai'/'ai-outline' resolve via TFButton.less classes; 'transparent'/'link' use Northstar's borderless `text` appearance.
        primary: variant === 'primary',
        text: variant === 'transparent' || variant === 'link',
        size: SIZE_MAP[size],
        icon: iconProp,
        iconPosition: iconAfterOnly ? 'after' : 'before',
        iconOnly: iconOnly,
        content: contentProp,
        loading: loading,
        "aria-busy": loading,
        disabled: disabled,
        fluid: fullWidth,
        onClick: onClick,
        className: cn('tf-button', "tf-button--".concat(variant), "tf-button--".concat(size), {
            'tf-button--disabled': disabled,
            'tf-button--icon-only': iconOnly
        }, className),
        // Order matters: variant color, then disabled cursor, then the call site's `style` last so it can always override.
        // `pointerEvents: 'auto'` undoes Northstar's disabled `pointer-events: none` so `cursor` and a wrapping `TooltipHost` still receive hover; the native `disabled` attribute already blocks clicks on its own.
        style: _object_spread({}, variantStyle(variant, disabled), disabled && {
            cursor: 'not-allowed',
            pointerEvents: 'auto'
        }, style),
        // An icon-only button has no visible label — fall back the accessible name to `title` when the
        // call site didn't set `aria-label` explicitly. Only applies when there's no label already (see above).
        "aria-label": (_rest_arialabel = rest['aria-label']) !== null && _rest_arialabel !== void 0 ? _rest_arialabel : iconOnly ? title : undefined
    }));
};
